import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import StoreNavigation from '../components/StoreNavigation';
import StoreFooter from '../components/StoreFooter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import { HelpCircle, Mail, MessageSquare, Truck, RefreshCcw, Ruler, Package, ArrowRight, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Support = () => {
  const { getCartItemCount } = useCart();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    orderNumber: '',
    subject: '',
    message: ''
  });

  const faqs = [
    {
      category: 'Sizing & Fit',
      icon: Ruler,
      questions: [
        {
          q: 'How do I find my size?',
          a: 'Use our detailed size chart on the Fit Guide page. Measure your bust, waist, and hip, then compare to our chart. Our suits are designed to fit over light clothing (t-shirt and pants).'
        },
        {
          q: 'What if I\'m between sizes?',
          a: 'If you\'re between sizes, we recommend sizing up, especially if you plan to layer underneath or want a looser fit.'
        },
        {
          q: 'Are the suits true to size?',
          a: 'Yes! Our suits are designed to women\'s proportions and are true to Australian sizing. Check our fit guide for detailed measurements.'
        }
      ]
    },
    {
      category: 'Shipping',
      icon: Truck,
      questions: [
        {
          q: 'How long does shipping take?',
          a: 'Standard shipping is 5-7 business days within Australia. Express shipping (2-3 business days) is available at checkout.'
        },
        {
          q: 'Do you offer free shipping?',
          a: 'Shipping is calculated at checkout based on your location within Australia.'
        },
        {
          q: 'Do you ship internationally?',
          a: 'Currently, we only ship within Australia. International shipping is coming soon!'
        }
      ]
    },
    {
      category: 'Returns & Exchanges',
      icon: RefreshCcw,
      questions: [
        {
          q: 'What is your return policy?',
          a: 'We offer a 30-day return policy. Items must be unworn, unwashed, and in original packaging with tags attached.'
        },
        {
          q: 'How do I exchange for a different size?',
          a: 'Email us at hello@vestaworkwear.com.au with your order number and the size you need. We\'ll arrange the exchange for you.'
        },
        {
          q: 'Who pays for return shipping?',
          a: 'Returns due to change of mind have a small return fee. If the item is faulty or we made an error, returns are free.'
        }
      ]
    },
    {
      category: 'Product Care',
      icon: Package,
      questions: [
        {
          q: 'How do I wash my suit?',
          a: 'Machine wash warm. Wash in a Vesta Wash Bag or inside out to protect Velcro closures. Close all zippers before washing. Hang to dry only - do not tumble dry. Do not bleach or iron.'
        },
        {
          q: 'How long will my suit last?',
          a: 'With proper care, your suit should last significantly longer than disposable options. Our premium weight fabric and reinforced construction is built for daily workshop use.'
        },
        {
          q: 'Can I repair my suit if it gets damaged?',
          a: 'We\'re currently developing repair solutions for our suits. In the meantime, contact us if you have any damage concerns and we\'ll do our best to help.'
        }
      ]
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact/support`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast({
          title: "Message Sent!",
          description: "We'll get back to you within 24-48 hours.",
        });
        setFormData({
          name: '',
          email: '',
          orderNumber: '',
          subject: '',
          message: ''
        });
      } else {
        throw new Error(data.message || 'Failed to send message');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again or email us directly.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen bg-white">
      <StoreNavigation cartItemCount={getCartItemCount()} />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#004D51] to-[#003338] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <HelpCircle className="w-16 h-16 mx-auto mb-6 text-[#F39C49]" />
            <h1 className="text-5xl md:text-7xl font-black mb-6">
              How Can We <span className="text-[#F39C49]">Help?</span>
            </h1>
            <p className="text-xl text-[#CFD2D4]">
              Find answers to common questions or get in touch with our support team.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 border-b-4 border-[#004D51]">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-4">
            <Link to="/fit" className="flex items-center justify-center p-4 bg-[#F3CEB2]/20 rounded-lg hover:bg-[#F3CEB2]/40 transition-all">
              <Ruler className="w-5 h-5 text-[#DB5A14] mr-2" />
              <span className="font-bold text-[#004D51]">Size Guide</span>
            </Link>
            <a href="#shipping" className="flex items-center justify-center p-4 bg-[#F3CEB2]/20 rounded-lg hover:bg-[#F3CEB2]/40 transition-all">
              <Truck className="w-5 h-5 text-[#DB5A14] mr-2" />
              <span className="font-bold text-[#004D51]">Shipping Info</span>
            </a>
            <a href="#returns" className="flex items-center justify-center p-4 bg-[#F3CEB2]/20 rounded-lg hover:bg-[#F3CEB2]/40 transition-all">
              <RefreshCcw className="w-5 h-5 text-[#DB5A14] mr-2" />
              <span className="font-bold text-[#004D51]">Returns</span>
            </a>
            <a href="#contact" className="flex items-center justify-center p-4 bg-[#F3CEB2]/20 rounded-lg hover:bg-[#F3CEB2]/40 transition-all">
              <Mail className="w-5 h-5 text-[#DB5A14] mr-2" />
              <span className="font-bold text-[#004D51]">Contact Us</span>
            </a>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-black text-[#004D51] mb-12 text-center">
            Frequently Asked Questions
          </h2>

          <div className="max-w-4xl mx-auto space-y-8">
            {faqs.map((category, categoryIndex) => (
              <div key={categoryIndex} id={category.category.toLowerCase().replace(/ & /g, '-')}>
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-[#DB5A14] rounded-lg flex items-center justify-center mr-3">
                    <category.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-2xl font-black text-[#004D51]">{category.category}</h3>
                </div>
                
                <Accordion type="single" collapsible className="space-y-2">
                  {category.questions.map((item, index) => (
                    <AccordionItem 
                      key={index} 
                      value={`${categoryIndex}-${index}`}
                      className="border-2 border-[#004D51] rounded-lg px-6 bg-white"
                    >
                      <AccordionTrigger className="text-left font-bold text-[#004D51] hover:text-[#DB5A14] py-4">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-[#545759] pb-4">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-24 bg-[#F3CEB2]/20">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <MessageSquare className="w-14 h-14 mx-auto mb-4 text-[#DB5A14]" />
              <h2 className="text-4xl font-black text-[#004D51] mb-4">
                Still Need Help?
              </h2>
              <p className="text-[#545759]">
                Send us a message and we'll get back to you within 24-48 hours.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 bg-white p-8 rounded-xl border-4 border-[#004D51]">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-black text-[#004D51] mb-2">
                    Your Name *
                  </label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="h-12 border-2 border-[#004D51] focus:border-[#DB5A14]"
                    placeholder="Your Name"
                    data-testid="support-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-black text-[#004D51] mb-2">
                    Email *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="h-12 border-2 border-[#004D51] focus:border-[#DB5A14]"
                    placeholder="your@email.com"
                    data-testid="support-email"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-black text-[#004D51] mb-2">
                    Order Number (if applicable)
                  </label>
                  <Input
                    name="orderNumber"
                    value={formData.orderNumber}
                    onChange={handleChange}
                    className="h-12 border-2 border-[#004D51] focus:border-[#DB5A14]"
                    placeholder="#12345"
                    data-testid="support-order"
                  />
                </div>
                <div>
                  <label className="block text-sm font-black text-[#004D51] mb-2">
                    Subject *
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full h-12 px-4 border-2 border-[#004D51] rounded-md focus:border-[#DB5A14] focus:outline-none bg-white"
                    data-testid="support-subject"
                  >
                    <option value="">Select topic...</option>
                    <option value="sizing">Sizing Help</option>
                    <option value="order">Order Status</option>
                    <option value="return">Return/Exchange</option>
                    <option value="product">Product Question</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-black text-[#004D51] mb-2">
                  Your Message *
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="min-h-32 border-2 border-[#004D51] focus:border-[#DB5A14]"
                  placeholder="How can we help?"
                  data-testid="support-message"
                />
              </div>

              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 bg-[#DB5A14] hover:bg-[#F39C49] text-white font-black text-lg disabled:opacity-70"
                data-testid="support-submit"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </form>

            <p className="text-center mt-6 text-[#545759]">
              Or email us directly at{' '}
              <a href="mailto:hello@vestaworkwear.com.au" className="text-[#DB5A14] font-bold hover:text-[#F39C49]">
                hello@vestaworkwear.com.au
              </a>
            </p>
          </div>
        </div>
      </section>

      <StoreFooter />
    </div>
  );
};

export default Support;
